/**
 * Static helper functions for the PL/SQL debugger (no hooks, no side effects).
 */

import type { PlSqlDebuggerTabContent, ProgramIdentity } from '../types';
import { isPlSqlDebuggerTab, isPlSqlEditorTab } from '../../core/types/workspaceTypes';
import type { useWorkspaceStore } from '../../core/store/workspaceStore';

/** Stable key for per-program breakpoint storage: OWNER.NAME.NAMESPACE */
export function programKey(p: { owner: string; name: string; namespace: number }): string {
  return `${p.owner.toUpperCase()}.${p.name.toUpperCase()}.${p.namespace}`;
}

/** Derive ProgramIdentity for the original tab object */
export function getOriginalProgramIdentity(content: PlSqlDebuggerTabContent): ProgramIdentity {
  if (content.objectType === 'anonymous') {
    return { owner: '', name: '', namespace: 0 };
  }
  const namespace = content.objectType === 'package' ? 2
    : content.objectType === 'trigger' ? 3
    : 1;
  return { owner: content.schema, name: content.objectName, namespace };
}

/** Get the currently displayed program identity (falls back to original tab object) */
export function getDisplayedProgramIdentity(content: PlSqlDebuggerTabContent): ProgramIdentity {
  return content.displayedProgram ?? getOriginalProgramIdentity(content);
}

/** Build DBMS_DEBUG program info for breakpoint sync — uses displayed program when set */
export function buildProgramInfo(content: PlSqlDebuggerTabContent) {
  const identity = getDisplayedProgramIdentity(content);
  return {
    namespace: identity.namespace,
    name: identity.name,
    owner: identity.owner,
  };
}

/**
 * Best-effort cleanup: stop then detach.
 * Use `terminated: true` when the program finished naturally — skips ABORT
 * (which would fail on an already-finished session) and calls detach directly.
 */
export async function cleanupSession(sessionKey: string, terminated = false) {
  try {
    if (terminated) {
      // Program already finished — ABORT would fail, just detach
      await window.electronAPI!.oracleDebugger.detach({ sessionKey });
    } else {
      await window.electronAPI!.oracleDebugger.stop({ sessionKey });
    }
  } catch {
    try {
      await window.electronAPI!.oracleDebugger.detach({ sessionKey });
    } catch { /* ignore */ }
  }
}

/**
 * Find an existing debugger tab for the same connection + schema.objectName.
 * Returns the tab ID if found, or null if no existing tab.
 */
export function findExistingDebuggerTab(
  tabs: ReturnType<typeof useWorkspaceStore.getState>['tabs'],
  schema: string,
  objectName: string,
  connectionId?: string,
): string | null {
  for (const tab of tabs) {
    if (isPlSqlDebuggerTab(tab)
      && tab.content.schema.toUpperCase() === schema.toUpperCase()
      && tab.content.objectName.toUpperCase() === objectName.toUpperCase()
      && (!connectionId || tab.connectionInfo?.connectionId === connectionId)) {
      return tab.id;
    }
  }
  return null;
}

/**
 * Find an existing plsql-editor tab for the same connection + schema.objectName.
 * Returns the tab ID if found, or null if no existing tab.
 */
export function findExistingEditorTab(
  tabs: ReturnType<typeof useWorkspaceStore.getState>['tabs'],
  schema: string,
  objectName: string,
  connectionId?: string,
): string | null {
  for (const tab of tabs) {
    // Skip virtual tabs embedded in Package Explorer — they're not real workspace tabs
    if (tab.id.includes('::pkg::')) continue;
    if (isPlSqlEditorTab(tab)
      && tab.content.schema.toUpperCase() === schema.toUpperCase()
      && tab.content.objectName.toUpperCase() === objectName.toUpperCase()
      && (!connectionId || tab.connectionInfo?.connectionId === connectionId)) {
      return tab.id;
    }
  }
  return null;
}

/**
 * Find which package member (procedure/function) the given line falls within.
 * Scans the source for PROCEDURE/FUNCTION declarations and returns the member
 * whose declaration starts at or before the given line (closest match).
 */
export function findMemberAtLine(
  source: string,
  line: number,
  members: Array<{ name: string; type: 'procedure' | 'function'; overloadIndex?: number }>,
): typeof members[number] | undefined {
  if (!source || !members?.length || line < 1) return undefined;

  const lines = source.split('\n');
  // Build a map of member name -> first declaration line
  const memberLines: Array<{ member: typeof members[number]; line: number }> = [];

  for (const member of members) {
    const keyword = member.type === 'procedure' ? 'PROCEDURE' : 'FUNCTION';
    const pattern = new RegExp(`\\b${keyword}\\s+${member.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    for (let i = 0; i < lines.length; i++) {
      if (pattern.test(lines[i])) {
        memberLines.push({ member, line: i + 1 });
        break;
      }
    }
  }

  if (!memberLines.length) return undefined;

  // Sort by line ascending
  memberLines.sort((a, b) => a.line - b.line);

  // Find the last member whose declaration line is <= the cursor line
  let match: typeof members[number] | undefined;
  for (const entry of memberLines) {
    if (entry.line <= line) {
      match = entry.member;
    } else {
      break;
    }
  }
  return match;
}

const SOURCE_LOAD_TIMEOUT_MS = 15_000;

export async function loadObjectSource(
  tabId: string,
  connectionId: string,
  schema: string,
  objectName: string,
  objectType: string,
  updateTabContent: ReturnType<typeof useWorkspaceStore.getState>['updateTabContent']
) {
  try {
    const fetchPromise = window.electronAPI!.oracleDebugger.getObjectSource({
      connectionId,
      schema,
      objectName,
      objectType: objectType as 'procedure' | 'function' | 'package' | 'trigger',
    });

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Source loading timed out — the database connection may be stale. Try again or reconnect.')), SOURCE_LOAD_TIMEOUT_MS)
    );

    const result = await Promise.race([fetchPromise, timeoutPromise]);

    if (result.success && result.source) {
      const contentUpdate: Partial<PlSqlDebuggerTabContent> = {
        source: result.source,
        isLoading: false,
        error: undefined,
      };

      // For packages, also fetch members to populate the start dialog
      if (objectType === 'package') {
        try {
          const details = await window.electronAPI!.oraclePackage.getDetails({
            connectionId, schema, packageName: objectName,
          });
          if (details.success && details.members) {
            contentUpdate.members = details.members;
          }
        } catch { /* non-critical — dialog will use generic placeholder */ }
      }

      updateTabContent(tabId, contentUpdate as Partial<PlSqlDebuggerTabContent>);
    } else {
      updateTabContent(tabId, {
        isLoading: false,
        error: result.error ?? 'Failed to load source',
      } as Partial<PlSqlDebuggerTabContent>);
    }
  } catch (error) {
    updateTabContent(tabId, {
      isLoading: false,
      error: error instanceof Error ? error.message : 'Failed to load source',
    } as Partial<PlSqlDebuggerTabContent>);
  }
}
